import { useState } from "preact/hooks";
import type { JSX } from "preact";
import { createFolder, deleteFolder, moveMessage, openCompose, renameFolder, selectMailbox } from "../actions";
import { currentMailbox, folderActionError, folderCreateParent, folderEditPath, mailboxes } from "../state";
import type { Mailbox, SpecialUse } from "../../shared/types";
import { Icon, type IconName } from "./icons";

const SPECIAL_ICON: Partial<Record<NonNullable<SpecialUse>, IconName>> = {
  inbox: "inbox",
  drafts: "drafts",
  sent: "sent",
  archive: "archive",
  junk: "flame",
  trash: "trash",
  flagged: "star",
  all: "archive",
};

function iconFor(m: Mailbox): IconName {
  if (m.specialUse && SPECIAL_ICON[m.specialUse]) return SPECIAL_ICON[m.specialUse]!;
  return "folder";
}

function pick(boxes: Mailbox[], uses: SpecialUse[]): Mailbox[] {
  return uses
    .map((u) => boxes.find((b) => b.specialUse === u))
    .filter((b): b is Mailbox => Boolean(b));
}

/** Replace the last path segment with a new name. */
function replaceLastSegment(path: string, delimiter: string, newName: string): string {
  const idx = path.lastIndexOf(delimiter);
  if (idx < 0) return newName;
  return path.slice(0, idx + delimiter.length) + newName;
}

/** Extract the display name (last segment) from an IMAP path. */
function lastSegment(path: string, delimiter: string): string {
  const idx = path.lastIndexOf(delimiter);
  return idx < 0 ? path : path.slice(idx + delimiter.length);
}

export function Sidebar(): JSX.Element {
  const [showMore, setShowMore] = useState(false);
  const [showFolders, setShowFolders] = useState(true);
  const [createInput, setCreateInput] = useState("");

  const boxes = mailboxes.value;

  const primary = pick(boxes, ["inbox", "drafts", "sent"]);
  const more = pick(boxes, ["archive", "junk", "trash"]);
  const custom = boxes.filter((b) => b.specialUse === null);

  // folderCreateParent: undefined = closed, null = root, string = nested
  const isCreating = folderCreateParent.value !== undefined;

  function handleCreateConfirm(): void {
    const name = createInput.trim();
    if (!name) return;
    setCreateInput("");
    void createFolder(name, folderCreateParent.value ?? undefined);
  }

  function handleCreateKeyDown(e: KeyboardEvent): void {
    if (e.key === "Enter") {
      handleCreateConfirm();
    } else if (e.key === "Escape") {
      folderCreateParent.value = undefined;
      setCreateInput("");
    }
  }

  return (
    <aside class="flex h-full w-64 shrink-0 flex-col overflow-hidden border-r border-subtle bg-ink-1">
      <div class="p-3">
        <button class="btn-accent w-full" title="New message" onClick={() => openCompose()}>
          <Icon name="plus" size={18} /> New message
        </button>
      </div>

      <nav class="flex-1 overflow-y-auto px-2 pb-2">
        {primary.map((m) => (
          <NavItem key={m.path} m={m} allMailboxes={boxes} />
        ))}

        {more.length > 0 && (
          <>
            <button
              onClick={() => setShowMore(!showMore)}
              class="mt-0.5 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-fg-dim transition-colors hover:bg-ink-3"
            >
              <Icon name={showMore ? "chevronDown" : "chevronRight"} size={16} />
              More
            </button>
            {showMore && more.map((m) => <NavItem key={m.path} m={m} allMailboxes={boxes} />)}
          </>
        )}

        {/* Folders section — always shown so user can create folders even when custom is empty */}
        <div class="mt-4">
          {/* Section header */}
          <div class="flex items-center gap-1 px-1">
            <button
              onClick={() => setShowFolders(!showFolders)}
              class="flex flex-1 items-center gap-1 rounded px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted transition-colors hover:bg-ink-3 hover:text-fg-dim"
            >
              <Icon name={showFolders ? "chevronDown" : "chevronRight"} size={12} />
              Folders
            </button>
            <button
              title="New folder"
              onClick={() => { folderCreateParent.value = null; setCreateInput(""); }}
              class="icon-btn h-6 w-6 rounded text-muted hover:text-fg"
            >
              <Icon name="plus" size={14} />
            </button>
          </div>

          {/* Error banner */}
          {folderActionError.value && (
            <p class="px-3 py-1 text-xs text-danger">{folderActionError.value}</p>
          )}

          {/* Inline create input */}
          {isCreating && showFolders && (
            <div class="px-2 py-1">
              <input
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                type="text"
                placeholder="Folder name…"
                value={createInput}
                onInput={(e) => setCreateInput((e.target as HTMLInputElement).value)}
                onKeyDown={handleCreateKeyDown}
                class="field py-1 px-2 text-xs"
              />
            </div>
          )}

          {/* Custom folder list */}
          {showFolders && custom.map((m) => (
            <NavItem key={m.path} m={m} showActions allMailboxes={boxes} />
          ))}
        </div>

        <div class="mt-4">
          <div class="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted">Labels</div>
          <p class="px-3 py-2 text-xs text-muted">No labels yet</p>
        </div>
      </nav>
    </aside>
  );
}

function NavItem({
  m,
  showActions,
  allMailboxes,
}: {
  m: Mailbox;
  showActions?: boolean;
  allMailboxes?: Mailbox[];
}): JSX.Element {
  const active = currentMailbox.value === m.path;
  const label = m.specialUse ?? m.name;
  const [dragOver, setDragOver] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [renameInput, setRenameInput] = useState("");

  const isRenaming = showActions && folderEditPath.value === m.path;

  // Detect sub-folders: any mailbox path starts with this path + delimiter
  const hasChildren = Boolean(
    allMailboxes && m.delimiter &&
    allMailboxes.some((b) => b.path !== m.path && b.path.startsWith(m.path + m.delimiter)),
  );

  function handleRenameStart(): void {
    setRenameInput(lastSegment(m.path, m.delimiter ?? "/"));
    folderEditPath.value = m.path;
    setMenuOpen(false);
  }

  function handleRenameConfirm(): void {
    const name = renameInput.trim();
    if (!name) return;
    const newPath = replaceLastSegment(m.path, m.delimiter ?? "/", name);
    void renameFolder(m.path, newPath);
  }

  function handleRenameKeyDown(e: KeyboardEvent): void {
    if (e.key === "Enter") {
      handleRenameConfirm();
    } else if (e.key === "Escape") {
      folderEditPath.value = null;
    }
  }

  function handleDeleteStart(): void {
    setDeleteConfirm(true);
    setMenuOpen(false);
  }

  function handleDeleteConfirm(): void {
    setDeleteConfirm(false);
    void deleteFolder(m.path);
  }

  return (
    <div class="relative">
      <button
        onClick={() => void selectMailbox(m.path)}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer!.dropEffect = "move"; setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          try {
            const raw = e.dataTransfer!.getData("text/plain");
            const { uid, mailbox: srcMailbox } = JSON.parse(raw) as { uid: number; mailbox: string };
            if (srcMailbox !== m.path) {
              void moveMessage(uid, m.path);
            }
          } catch { /* ignore malformed data */ }
        }}
        class={`group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
          active ? "bg-accent/15 text-fg" : "text-fg-dim hover:bg-ink-3"
        } ${dragOver ? "ring-2 ring-accent bg-accent/20" : ""}`}
      >
        <Icon
          name={iconFor(m)}
          size={18}
          class={active ? "text-accent-400" : "text-muted group-hover:text-fg"}
        />

        {/* Inline rename input replaces the label when renaming */}
        {isRenaming ? (
          <input
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            type="text"
            value={renameInput}
            onInput={(e) => setRenameInput((e.target as HTMLInputElement).value)}
            onKeyDown={handleRenameKeyDown}
            onClick={(e) => e.stopPropagation()}
            class="field flex-1 py-0.5 px-1 text-xs"
          />
        ) : (
          <span class="flex-1 truncate text-left capitalize">{label}</span>
        )}

        {m.unread > 0 && !isRenaming && (
          <span
            class={`min-w-[1.25rem] rounded-full px-1.5 py-0.5 text-center text-[11px] font-semibold ${
              active ? "bg-accent text-fg-on-accent" : "bg-ink-4 text-fg-dim"
            }`}
          >
            {m.unread}
          </span>
        )}

        {/* Kebab menu trigger — only for custom folders, hidden by default */}
        {showActions && !isRenaming && (
          <button
            title="Folder options"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((o) => !o);
              setDeleteConfirm(false);
            }}
            class="opacity-0 group-hover:opacity-100 focus:opacity-100 icon-btn h-5 w-5 rounded text-muted hover:text-fg shrink-0"
          >
            <Icon name="dots" size={14} />
          </button>
        )}
      </button>

      {/* Kebab dropdown */}
      {showActions && menuOpen && (
        <div
          tabIndex={-1}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setMenuOpen(false);
            }
          }}
          class="glass absolute right-0 top-full z-10 min-w-[120px] rounded-lg border border-subtle py-1 shadow-lg"
        >
          <button
            onClick={() => handleRenameStart()}
            class="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-fg-dim hover:bg-ink-3 hover:text-fg"
          >
            Rename
          </button>
          <button
            onClick={() => handleDeleteStart()}
            class="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-danger hover:bg-ink-3"
          >
            Delete
          </button>
        </div>
      )}

      {/* Delete confirmation */}
      {showActions && deleteConfirm && (
        <div class="glass mx-2 mb-1 rounded-lg border border-subtle px-3 py-2 text-xs">
          {hasChildren && (
            <p class="mb-1 text-warn">This folder may contain sub-folders.</p>
          )}
          <p class="mb-2 text-fg-dim">
            Delete &ldquo;{lastSegment(m.path, m.delimiter ?? "/")}&rdquo;?
          </p>
          <div class="flex gap-2">
            <button
              onClick={() => handleDeleteConfirm()}
              class="rounded bg-danger px-2 py-0.5 text-xs font-semibold text-fg-on-accent hover:opacity-80"
            >
              Confirm
            </button>
            <button
              onClick={() => setDeleteConfirm(false)}
              class="rounded px-2 py-0.5 text-xs text-fg-dim hover:bg-ink-3"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
